import React from "react";
import {
    Button as ChakraButton,
    ButtonProps,
    IconButton as ChakraIconButton,
    IconButtonProps,
    Input as ChakraInput,
    InputProps,
    Select as ChakraSelect,
    SelectProps,
    Textarea as ChakraTextarea,
    TextareaProps,
    Th as ChakraTh,
    TableColumnHeaderProps,
    Badge as ChakraBadge,
    BadgeProps,
} from "@chakra-ui/react";

/**
 * The kit's controls — the 44px touch-target floor, applied once instead of at ~100 call sites.
 *
 * POLISH v1 rule 2 sets a 44px minimum touch target. Chakra's own sizes are all under it
 * (`xs` 24px, `sm` 32px, `md` 40px; only `lg` clears it at 48px), so every `size="sm"` button in
 * this module was a 32px tap target — 37 of them on the bill page alone. Annotating each one
 * leaves the next hand-written button short again, so the floor lives HERE and the pages import
 * these instead of Chakra's.
 *
 * `minH`/`minW` is deliberately a *minimum*, not a height: a button keeps whatever `size` the page
 * gave it for type scale and padding, and only grows if that size would land under the floor. Type
 * is floored the same way — `xs` is raised to 14px, and every larger size keeps its own scale. Any
 * caller can still pass its own `minH` — the spread below comes after the default — which is how
 * the handful of genuinely non-tappable cases (a static inline affordance inside a dense table)
 * opt out explicitly rather than by accident.
 *
 * Refs are forwarded because `SafeTooltip` clones its child and needs one; a wrapper that swallowed
 * the ref would make every tooltip in the module silently stop positioning.
 */
export const TOUCH_MIN = "44px";

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ size, fontSize = size === "xs" ? "sm" : undefined, sx, ...props }, ref) => (
        <ChakraButton
            ref={ref}
            size={size}
            minH={TOUCH_MIN}
            // The type floor works the same way: `size="xs"` sets 12px type, and the theme writes
            // that into the same emotion class after any style prop (see `Th`), so seventeen
            // buttons in this module kept 12px labels on 44px targets. Only `xs` is under 14px in
            // the theme, so only `xs` — or a caller's own `fontSize` — gets the `&&` override;
            // `sm`/`md`/`lg` keep their theme type (14/16/18px) untouched.
            sx={{ ...(fontSize !== undefined && { "&&": { fontSize } }), ...(sx as Record<string, unknown>) }}
            {...props}
        />
    ),
);
Button.displayName = "KitButton";

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => (
    <ChakraIconButton ref={ref} minH={TOUCH_MIN} minW={TOUCH_MIN} {...props} />
));
IconButton.displayName = "KitIconButton";

export const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
    <ChakraInput ref={ref} minH={TOUCH_MIN} {...props} />
));
Input.displayName = "KitInput";

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => (
    <ChakraSelect ref={ref} minH={TOUCH_MIN} {...props} />
));
Select.displayName = "KitSelect";

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
    <ChakraTextarea ref={ref} minH={TOUCH_MIN} {...props} />
));
Textarea.displayName = "KitTextarea";

/**
 * A table header that obeys the module's own two rules.
 *
 * Chakra's `Th` theme sets `fontSize: xs` (12px) and `textTransform: uppercase`. Neither is
 * visible in the page source, so a readability sweep that greps for `fontSize="xs"` reports the
 * module clean while every one of its 70 table headers renders at 12px — the real browser is what
 * caught this, measuring computed styles on the live bill page. Uppercasing is the same kind of
 * silent edit: the pages already write `<Th>Client Name</Th>`, and the theme shouts it back as
 * CLIENT NAME. The floor and the author's own words are restored here, once.
 */
export const Th = React.forwardRef<HTMLTableCellElement, TableColumnHeaderProps>(
    ({ fontSize = "sm", textTransform = "none", letterSpacing = "normal", sx, ...props }, ref) => (
        <ChakraTh
            ref={ref}
            // `&&` doubles the class selector. A plain style prop is NOT enough here: Chakra emits
            // the Table theme's `th` size style into the SAME emotion class, after the prop, so the
            // declaration that wins is the last one written rather than the most specific — the
            // header still measured 12px in the browser with `fontSize="sm"` set both by this kit
            // and by the page. Doubling the selector settles it by specificity instead of order.
            // `textTransform` happens to win as a plain prop; it travels with the others so the
            // whole header style is decided in one place.
            sx={{ "&&": { fontSize, textTransform, letterSpacing }, ...(sx as Record<string, unknown>) }}
            {...props}
        />
    ),
);
Th.displayName = "KitTh";

/**
 * A badge that can be read, in the words the page wrote.
 *
 * Chakra's `Badge` base style is `fontSize: xs` (12px) and `textTransform: uppercase` — the same
 * silent pair as `Th`, and the reason the live payment pages showed DEFAULT, ACTIVE and PAID
 * shouted at 12px while the module's status *pills* had already been reworded. A badge that says
 * what the page says, at the readability floor, keeps one vocabulary on screen.
 *
 * Status badges that carry a module enum should use `StatusPill` with `statusWords` instead; this
 * is for the plain labels (a card's "Default", a plan's "Most popular").
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    ({ fontSize = "sm", textTransform = "none", sx, ...props }, ref) => (
        <ChakraBadge
            ref={ref}
            sx={{ "&&": { fontSize, textTransform }, ...(sx as Record<string, unknown>) }}
            {...props}
        />
    ),
);
Badge.displayName = "KitBadge";
